/**
 * Therapy API-to-Client Adapter
 * 
 * Transforms TherapyDto from API into simplified ClientTherapy
 */

import { TherapyDto } from "../../../../../api-client";
import { TherapyExt } from "../../../Calendar.type";
import { ClientTherapy } from "../types";
import { isPassiveTherapy, methodColorKey } from "../../../../../helpers/slotTherapy";

/**
 * Transform API TherapyDto to simplified ClientTherapy
 * Note: TherapyDto from API doesn't have duration/name, but TherapyExt (used in UI) does
 */
export const adaptTherapyFromApi = (therapy: TherapyDto | TherapyExt): ClientTherapy => {
  const abbreviation = therapy.abbreviation || '';
  
  // Handle both API DTO and extended therapy types
  const duration = (therapy as TherapyExt).duration || 30;
  const name = (therapy as any).name || '';
  // The backend always delivers the therapy id (Therapy.id is a primary column and
  // TherapyRxDto.therapy is the full entity). Only the generated api-client marks it
  // optional, so we normalize once here — this is the single boundary where the id
  // becomes the required `ClientTherapy.id` that downstream credential checks rely on.
  const id = (therapy as TherapyDto).id ?? '';
  
  return {
    id,
    abbreviation,
    duration,
    name,
    // MD-232: a data flag on the line item, never an abbreviation test —
    // the set differs per discipline and Heiße Rolle is Wärme but not passive.
    isHeatTreatment: isPassiveTherapy(therapy as { isPassive?: boolean }),
    maxParticipants: (therapy as any).maxParticipants,
    // MD-232: carry the catalog identity through so downstream surfaces colour
    // by it. `therapyMethodId` is on the line item; `colorKey` is what
    // slotTherapy's term fallback supplies when no line item exists yet.
    colorKey: (therapy as any).therapyMethodId
      ? methodColorKey((therapy as any).therapyMethodId)
      : // Already namespaced when it came from slotTherapy's term fallback.
        (therapy as any).colorKey ?? undefined,
    // MD-328: the appointment repo already selects `therapy.pricingBasis`; it
    // was simply dropped here. It is what decides whether this treatment's
    // duration may be resized — for a per-interval position the duration IS
    // the billed quantity.
    pricingBasis: (therapy as any).pricingBasis ?? undefined,
  };
};